/**
 * Markup shape validation utilities
 * Feature: 049-critique-photo-markup
 *
 * Validates shape geometry, coordinates, and markup constraints
 */

import type {
  MarkupShape,
  CircleGeometry,
  RectangleGeometry,
  ArrowGeometry,
} from '@/types/markup.types';
import { MARKUP_CONSTRAINTS } from '@/types/markup.types';

/**
 * Clamp a value to a range, handling floating-point rounding errors
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Validation result with optional error message
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Click threshold - shapes smaller than this are considered accidental clicks
 * and should be silently ignored rather than showing validation errors
 */
const CLICK_THRESHOLD = 0.1; // percentage units

/**
 * Check if shape dimensions indicate an accidental click vs intentional draw
 * Returns true if shape is too small to be intentional (just a click)
 */
export function isAccidentalClick(shape: MarkupShape): boolean {
  switch (shape.type) {
    case 'circle':
      return shape.geometry.r < CLICK_THRESHOLD;
    case 'rectangle':
    case 'spotlight':
      return (
        shape.geometry.width < CLICK_THRESHOLD || shape.geometry.height < CLICK_THRESHOLD
      );
    case 'arrow':
      const length = Math.sqrt(
        Math.pow(shape.geometry.x2 - shape.geometry.x1, 2) +
          Math.pow(shape.geometry.y2 - shape.geometry.y1, 2)
      );
      return length < CLICK_THRESHOLD;
    default:
      return false;
  }
}

/**
 * Validate circle geometry, clamping to bounds for floating-point rounding
 *
 * BUG FIX (#480): Now validates using image-relative coordinates (0-100 for both X and Y)
 * instead of viewBox-relative coordinates
 *
 * BUG FIX: Clamp values to bounds instead of failing for floating-point rounding errors
 * at image edges. Only clamp values DOWN (to fit within bounds), not UP (which would
 * change user intent).
 */
export function validateCircle(
  geometry: CircleGeometry,
  _viewBoxHeight: number = 100 // Kept for backwards compatibility, no longer used
): ValidationResult {
  // Clamp coordinates to valid range (handles floating-point rounding at edges)
  geometry.cx = clamp(geometry.cx, 0, 100);
  geometry.cy = clamp(geometry.cy, 0, 100);

  // Only clamp radius if it exceeds max (don't clamp up for small radii)
  if (geometry.r > 50) {
    geometry.r = 50;
  }

  // Check minimum radius (this is user intent, not a rounding error)
  if (geometry.r < MARKUP_CONSTRAINTS.MIN_CIRCLE_RADIUS) {
    return {
      valid: false,
      error: `Circle radius must be ${MARKUP_CONSTRAINTS.MIN_CIRCLE_RADIUS}-50`,
    };
  }

  return { valid: true };
}

/**
 * Validate rectangle geometry, clamping to bounds for floating-point rounding
 *
 * BUG FIX (#480): Now validates using image-relative coordinates (0-100 for both X and Y)
 * instead of viewBox-relative coordinates
 *
 * BUG FIX: Clamp values to bounds instead of failing for floating-point rounding errors
 * at image edges. Only clamp values DOWN (to fit within bounds), not UP.
 */
export function validateRectangle(
  geometry: RectangleGeometry,
  _viewBoxHeight: number = 100 // Kept for backwards compatibility, no longer used
): ValidationResult {
  // Clamp coordinates to valid range (handles floating-point rounding at edges)
  geometry.x = clamp(geometry.x, 0, 100);
  geometry.y = clamp(geometry.y, 0, 100);

  // Cap width/height at 100 if they exceed it
  if (geometry.width > 100) {
    geometry.width = 100;
  }
  if (geometry.height > 100) {
    geometry.height = 100;
  }

  // Clamp to fit within image bounds (handles edge rounding errors)
  if (geometry.x + geometry.width > 100) {
    geometry.width = 100 - geometry.x;
  }
  if (geometry.y + geometry.height > 100) {
    geometry.height = 100 - geometry.y;
  }

  // Check minimum size (this is user intent, not a rounding error)
  if (geometry.width < MARKUP_CONSTRAINTS.MIN_RECTANGLE_SIZE) {
    return {
      valid: false,
      error: `Rectangle width must be ${MARKUP_CONSTRAINTS.MIN_RECTANGLE_SIZE}-100`,
    };
  }
  if (geometry.height < MARKUP_CONSTRAINTS.MIN_RECTANGLE_SIZE) {
    return {
      valid: false,
      error: `Rectangle height must be ${MARKUP_CONSTRAINTS.MIN_RECTANGLE_SIZE}-100`,
    };
  }

  return { valid: true };
}

/**
 * Validate arrow geometry, clamping to bounds for floating-point rounding
 *
 * BUG FIX (#480): Now validates using image-relative coordinates (0-100 for both X and Y)
 * instead of viewBox-relative coordinates
 *
 * BUG FIX: Clamp values to bounds instead of failing for floating-point rounding errors
 */
export function validateArrow(
  geometry: ArrowGeometry,
  _viewBoxHeight: number = 100 // Kept for backwards compatibility, no longer used
): ValidationResult {
  // Clamp coordinates to valid range (handles floating-point rounding)
  geometry.x1 = clamp(geometry.x1, 0, 100);
  geometry.y1 = clamp(geometry.y1, 0, 100);
  geometry.x2 = clamp(geometry.x2, 0, 100);
  geometry.y2 = clamp(geometry.y2, 0, 100);

  // Check minimum length
  const length = Math.sqrt(
    Math.pow(geometry.x2 - geometry.x1, 2) + Math.pow(geometry.y2 - geometry.y1, 2)
  );

  if (length < MARKUP_CONSTRAINTS.MIN_ARROW_LENGTH) {
    return {
      valid: false,
      error: `Arrow must be at least ${MARKUP_CONSTRAINTS.MIN_ARROW_LENGTH}% long`,
    };
  }

  return { valid: true };
}

/**
 * Validate a single shape (delegates to type-specific validators)
 */
export function validateShape(
  shape: MarkupShape,
  viewBoxHeight: number = 100
): ValidationResult {
  switch (shape.type) {
    case 'circle':
      return validateCircle(shape.geometry, viewBoxHeight);
    case 'rectangle':
      return validateRectangle(shape.geometry, viewBoxHeight);
    case 'arrow':
      return validateArrow(shape.geometry, viewBoxHeight);
    default:
      return { valid: false, error: 'Unknown shape type' };
  }
}

/**
 * Validate entire markup object (all shapes)
 */
export function validateMarkup(
  shapes: MarkupShape[],
  viewBoxHeight: number = 100
): ValidationResult {
  // Check shape count
  if (shapes.length > MARKUP_CONSTRAINTS.MAX_SHAPES) {
    return {
      valid: false,
      error: `Maximum ${MARKUP_CONSTRAINTS.MAX_SHAPES} shapes allowed`,
    };
  }

  // Check sequential numbering (1-N)
  const numbers = shapes.map((s) => s.number).sort((a, b) => a - b);
  for (let i = 0; i < numbers.length; i++) {
    if (numbers[i] !== i + 1) {
      return { valid: false, error: 'Shape numbers must be sequential 1-N' };
    }
  }

  // Validate each shape
  for (const shape of shapes) {
    const result = validateShape(shape, viewBoxHeight);
    if (!result.valid) {
      return result;
    }
  }

  return { valid: true };
}

/**
 * Renumber shapes using push-down algorithm
 * When shape at oldIndex is moved to newIndex, affected shapes shift
 */
export function renumberShapes(
  shapes: MarkupShape[],
  shapeNumber: number,
  newNumber: number
): MarkupShape[] {
  // Find shape and its current position
  const oldIndex = shapes.findIndex((s) => s.number === shapeNumber);
  if (oldIndex === -1) {
    return shapes; // Shape not found, return unchanged
  }

  const newIndex = newNumber - 1; // Array is 0-indexed

  // Remove shape from current position
  const newShapes = [...shapes];
  const [shape] = newShapes.splice(oldIndex, 1);

  // Insert at new position (auto-shifts others down)
  newShapes.splice(newIndex, 0, shape);

  // Reassign all numbers (1-based)
  newShapes.forEach((s, i) => {
    s.number = i + 1;
  });

  return newShapes;
}

/**
 * Delete shape and renumber remaining shapes sequentially
 */
export function deleteShape(shapes: MarkupShape[], shapeId: string): MarkupShape[] {
  const newShapes = shapes
    .filter((s) => s.id !== shapeId)
    .map((s, index) => ({ ...s, number: index + 1 }));

  return newShapes;
}
